import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Activity, CheckCircle2, Circle, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_LABELS = {
  nano_banana: 'Image Generation (Nano Banana)',
  sora_2: 'Video Generation (Sora 2)',
  openai: 'Text / Reasoning (GPT)',
  elevenlabs: 'Voice (ElevenLabs)',
  heygen: 'Avatars (HeyGen)',
};

export default function StatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/status`);
      setStatus(r.data);
      setLastFetched(new Date());
    } catch {
      setStatus({ status: 'degraded', providers: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'System Status · Delphi AI';
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const allOperational = status && Object.values(status.providers || {}).every(p => p.status === 'operational');

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="status-page">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Activity className="w-5 h-5 text-white" /></div>
                  <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>System Status</h1>
                </div>
                <p className="text-sm text-white/50 ml-13">Real-time availability of Delphi&apos;s API and partner provider integrations.</p>
              </div>
              <button onClick={load} disabled={loading} data-testid="status-refresh" className="text-xs text-white/40 hover:text-white/80 transition-colors flex items-center gap-1.5">
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Top-level status */}
          <div className={`rounded-xl border p-6 mb-6 ${allOperational ? 'border-emerald-500/30 bg-emerald-500/[0.06]' : 'border-amber-500/30 bg-amber-500/[0.06]'}`} data-testid="status-overall">
            <div className="flex items-center gap-3">
              {loading && !status ? <Loader2 className="w-6 h-6 text-white/60 animate-spin" /> :
                allOperational ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> :
                <Circle className="w-6 h-6 text-amber-400 fill-amber-400/40" />}
              <div>
                <p className="text-lg font-bold text-white">
                  {loading && !status ? 'Checking…' : allOperational ? 'All systems operational' : 'Some services degraded'}
                </p>
                <p className="text-xs text-white/50 mt-0.5">
                  {lastFetched ? `Last checked ${lastFetched.toLocaleTimeString()}` : 'Loading…'}
                </p>
              </div>
            </div>
          </div>

          {/* Providers */}
          <h2 className="text-xs uppercase tracking-[0.25em] text-orange-400/70 font-semibold mb-3">Provider Integrations</h2>
          <div className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 overflow-hidden" data-testid="provider-list">
            {Object.entries(status?.providers || {}).map(([key, p], idx, arr) => (
              <div key={key} className={`flex items-center justify-between p-4 ${idx < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`} data-testid={`provider-${key}`}>
                <div className="flex items-center gap-3">
                  {p.status === 'operational'
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    : <Circle className="w-4 h-4 text-zinc-500" />}
                  <span className="text-sm text-white/90">{PROVIDER_LABELS[key] || key}</span>
                </div>
                <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded ${
                  p.status === 'operational' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-700/40 text-zinc-400'
                }`}>{p.status}</span>
              </div>
            ))}
            {!status && (
              <div className="p-6 text-center text-white/30 text-sm">Loading provider status…</div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-white/40">
            <span>API version {status?.version || '—'}</span>
            <Link to="/contact" className="hover:text-white/80">Something broken? <span className="underline">Contact us</span></Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
